import React, { useContext, useEffect } from "react";
import { Badge, Box, Button, Chip, Divider, Modal, Paper, Stack, Typography } from "@mui/material";
import MyIconButton, { ClearButton } from "../components/MyIconButton";
import CommentIcon from "@mui/icons-material/Comment";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import { ReviewContext } from "../../../provider/MyContexts";
import { StringObj } from "../../../type";
import { CheckBox, CheckBoxOutlined, Visibility, VisibilityOff } from "@mui/icons-material";
import { get } from "lodash";
import { questionKeyLabels } from "../../../config";
import { NavigateBefore, NavigateNext } from "@mui/icons-material";
import { flgLabels } from "../../../commands/review/excel";

type Props = {
  handleCommentOpen: () => void;
};

const detailLabels: StringObj = {
  problem: "問題点",
  proposedRevision: "修正案",
};

export default function ReviewDetailWindow(props: Props) {
  const { handleCommentOpen } = props;

  const {
    reviews,
    reviewState,
    handleDismiss,
    handleDone,
    selectedId,
    handleSelectId,
    moveNeighborReview,
  } = useContext(ReviewContext);

  const isDoneRow =
    selectedId && reviews[reviewState].find((i) => i.id == selectedId)?.done ? true : false;
  const isDismissRow =
    selectedId && reviews[reviewState].find((i) => i.id == selectedId)?.dismiss ? true : false;

  const [review, setReview] = React.useState<StringObj | null>(null);

  useEffect(() => {
    if (reviews[reviewState]) {
      setReview(reviews[reviewState].find((i) => i.id == selectedId) || null);
    }
  }, [selectedId, reviews, reviewState]);

  const questionCommands = [
    {
      label: "自動修正",
      icon: <AutoFixHighIcon fontSize="small" />,
      color: "#333",
      disabled: true,
    },
    {
      label: "コメント",
      icon:
        review && review.comments && review.comments.length ? (
          <Badge
            variant="dot"
            color="primary"
            sx={{
              width: 18,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <CommentIcon fontSize="small" />
          </Badge>
        ) : (
          <CommentIcon fontSize="small" />
        ),
      color: "#333",
      clickFunction: handleCommentOpen,
      disabled: false,
    },
    {
      label: isDismissRow ? "非表示" : "表示",
      icon: isDismissRow ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />,
      clickFunction: () => handleDismiss(selectedId, reviewState as "excel" | "llm"),
      color: isDismissRow ? "primary.main" : "secondary.main",
      disabled: false,
    },
    {
      label: isDoneRow ? "対応済み" : "未対応",
      icon: isDoneRow ? <CheckBox fontSize="small" /> : <CheckBoxOutlined fontSize="small" />,
      clickFunction: () => handleDone(selectedId, reviewState as "excel" | "llm"),
      color: isDoneRow ? "primary.main" : "secondary.main",
      disabled: false,
    },
  ];

  const navs = [
    { label: "前へ", icon: <NavigateBefore fontSize="small" />, id: "prev" },
    { label: "次へ", icon: <NavigateNext fontSize="small" />, id: "next" },
  ];

  if (!review) {
    return <></>;
  }

  return (
    <Modal
      open={true}
      onClose={() => {
        handleSelectId(undefined);
      }}
      disableAutoFocus
    >
      {/* <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        height: "100vh",
        px: 2,
      }}
      onClick={() => handleModalClose()}
    > */}

      <Paper
        variant="outlined"
        sx={{
          // width: "calc(100vw - 32px)",
          // padding: 1,
          // border: `1px solid #CCCCCC`,
          // maxHeight: "232px",
          // overflowY: "auto",
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          height: "72vw",
          width: "80vw",
          bgcolor: "background.paper",
          p: 2,
          borderRadius: 2,
          overflowY: "auto",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Stack
          paddingX={1}
          position={"relative"}
          direction={"row"}
          justifyContent={"space-between"}
          alignItems={"center"}
        >
          {/* <Typography variant="body2">チェック結果詳細</Typography> */}
          <Stack direction={"column"} height={72} alignContent={"center"}>
            <Typography variant="label_L" fontWeight={600} marginY={1}>
              {get(
                questionKeyLabels,
                review.reviewPoint,
                get(flgLabels, review.reviewPoint, review.reviewPoint)
              )}
            </Typography>
            <Typography variant="label_M">{`該当箇所: ${review.target}`}</Typography>
          </Stack>
          <Chip
            label={review.must ? "必須" : "任意"}
            size="small"
            sx={{
              marginRight: "40px",
            }}
          />
          <Stack position={"absolute"} right={0} top={0} direction={"row"}>
            <ClearButton onClick={() => handleSelectId("")} />
          </Stack>
        </Stack>

        <Divider flexItem />
        <Stack direction={"column"} sx={{ paddingX: 1, marginY: 1, gap: 2 }} flex={1}>
          {Object.keys(detailLabels).map((key, index) => {
            if (!review[key]) {
              return <div key={index}></div>;
            }
            return (
              <Stack key={index} direction={"column"} spacing={0.25} flex={1}>
                <Typography variant="label_S">{detailLabels[key]}</Typography>
                <Typography variant="body_S">{review[key]}</Typography>
              </Stack>
            );
          })}
        </Stack>
        <Divider flexItem />
        <Stack
          position={"relative"}
          direction={"row"}
          spacing={1}
          marginTop={1}
          sx={{
            position: "sticky",
            bottom: 0,
          }}
        >
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              backdropFilter: "blur(8px)",
              WebkitBackdropFilter: "blur(8px)", // Safari 対応
              backgroundColor: "#FFFFFF",
              opacity: 0.88,
              zIndex: 0,
            }}
          />
          <Stack direction={"row"} spacing={0.5} alignItems={"center"} position={"relative"}>
            {navs.map((nav, index) => (
              <Button
                key={index}
                variant="text"
                size="small"
                onClick={() => moveNeighborReview(nav.id as "prev" | "next")}
                sx={{
                  color: "#333",
                }}
              >
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  {nav.id == "prev" ? nav.icon : <></>}
                  <Typography variant="label_S">{nav.label}</Typography>
                  {nav.id == "next" ? nav.icon : <></>}
                </Stack>
              </Button>
            ))}
          </Stack>
          <Stack flex={1}></Stack>

          <Stack direction={"row"} spacing={0.5}>
            {questionCommands.map((obj, index) => (
              <MyIconButton
                key={index}
                icon={obj.icon}
                label={obj.label}
                clickFunction={obj.clickFunction}
                color={obj.color}
                disabled={obj.disabled}
              />
            ))}
          </Stack>
        </Stack>
      </Paper>
      {/* </Stack> */}
    </Modal>
  );
}

// <Paper
//   variant="outlined"
//   sx={{
//     // width: "calc(100vw - 32px)",
//     // padding: 1,
//     // border: `1px solid #CCCCCC`,
//     // maxHeight: "232px",
//     // overflowY: "auto",
//     position: "absolute",
//     top: "50%",
//     left: "50%",
//     transform: "translate(-50%, -50%)",
//     height: "64vw",
//     width: "80vw",
//     bgcolor: "background.paper",
//     p: 4,
//     borderRadius: 2,
//     overflowY: "auto",
//   }}
// >
//   <Stack
//     paddingX={1}
//     position={"relative"}
//     direction={"row"}
//     justifyContent={"space-between"}
//     alignItems={"center"}
//   >
//     {/* <Typography variant="body2">チェック結果詳細</Typography> */}
//     <Stack direction={"column"} height={72} alignContent={"center"}>
//       <Typography variant="label_L" fontWeight={600} marginY={1}>
//         {get(
//           questionKeyLabels,
//           review.reviewPoint,
//           get(flgLabels, review.reviewPoint, review.reviewPoint)
//         )}
//       </Typography>
//       <Typography variant="label_M">{`該当箇所: ${review.target}`}</Typography>
//     </Stack>
//     <Chip
//       label={review.must ? "必須" : "任意"}
//       size="small"
//       sx={{
//         marginRight: "40px",
//       }}
//     />
//     <Stack position={"absolute"} right={0} top={0} direction={"row"}>
//       <ClearButton onClick={() => handleSelectId("")} />
//     </Stack>
//   </Stack>

//   <Divider flexItem />
//   <Stack direction={"row"} sx={{ paddingX: 1, marginY: 1, gap: 2 }}>
//     {Object.keys(detailLabels).map((key, index) => {
//       if (!review[key]) {
//         return <></>;
//       }
//       return (
//         <Stack key={index} direction={"column"} spacing={0.25} flex={1}>
//           <Typography variant="label_S">{detailLabels[key]}</Typography>
//           <Typography variant="body_S">{review[key]}</Typography>
//         </Stack>
//       );
//     })}
//   </Stack>
//   <Divider flexItem />
//   <Stack
//     position={"relative"}
//     direction={"row"}
//     spacing={1}
//     marginTop={1}
//     sx={{
//       position: "sticky",
//       bottom: 0,
//     }}
//   >
//     <Box
//       sx={{
//         position: "absolute",
//         top: 0,
//         left: 0,
//         width: "100%",
//         height: "100%",
//         backdropFilter: "blur(8px)",
//         WebkitBackdropFilter: "blur(8px)", // Safari 対応
//         backgroundColor: "#FFFFFF",
//         opacity: 0.88,
//         zIndex: 0,
//       }}
//     />
//     <Stack direction={"row"} spacing={0.5} alignItems={"center"} position={"relative"}>
//       {navs.map((nav, index) => (
//         <Button
//           key={index}
//           variant="text"
//           size="small"
//           onClick={() => moveNeighborReview(nav.id as "prev" | "next")}
//           sx={{
//             color: "#333",
//           }}
//         >
//           <Stack direction="row" alignItems="center" spacing={0.5}>
//             {nav.id == "prev" ? nav.icon : <></>}
//             <Typography variant="label_S">{nav.label}</Typography>
//             {nav.id == "next" ? nav.icon : <></>}
//           </Stack>
//         </Button>
//       ))}
//     </Stack>
//     <Stack flex={1}></Stack>

//     <Stack direction={"row"} spacing={0.5}>
//       {questionCommands.map((obj, index) => (
//         <MyIconButton
//           key={index}
//           icon={obj.icon}
//           label={obj.label}
//           clickFunction={obj.clickFunction}
//           color={obj.color}
//           disabled={obj.disabled}
//         />
//       ))}
//     </Stack>
//   </Stack>
// </Paper>
